import React, { useEffect, useRef, useState } from 'react';
import { ui } from '../../ui';
import { Category } from '../../api';

interface CategoryMultiSelectProps {
  categories: Category[];
  selected: number[];
  onChange: (ids: number[]) => void;
  // Distinguishes element ids when this component is rendered more than once on a page
  // (Report 1's filter and Report 4's category selector both use it).
  idPrefix: string;
}

// Shared category filter (FR-1.4 / FR-4.2), rendered as a compact multiselect dropdown so it
// doesn't eat page space. OR logic — a call matches if it carries at least one of the selected
// categories. No selection = no filter (every call, including uncategorized ones); selecting
// one or more categories excludes uncategorized calls. Selection applies immediately on
// toggle — there's no separate Apply step.
export const CategoryMultiSelect: React.FC<CategoryMultiSelectProps> = ({ categories, selected, onChange, idPrefix }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const activeCategories = categories.filter(c => c.IsActive);
  const selectedCategories = activeCategories.filter(c => selected.includes(c.CategoryID));
  const summaryLabel =
    selectedCategories.length === 0 ? 'All reason codes'
      : selectedCategories.length === 1 ? selectedCategories[0].Name
        : `${selectedCategories.length} selected`;

  const toggle = (id: number) =>
    onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);

  // Close on outside click or Escape (returning focus to the trigger); a re-click of the
  // trigger itself is handled by its own onClick toggle below, not this listener.
  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const panelId = `${idPrefix}-categories-panel`;
  const labelId = `${idPrefix}-categories-label`;

  return (
    <div ref={containerRef} className="relative inline-block">
      <span id={labelId} className={ui.label}>Categories</span>
      <button
        ref={triggerRef}
        type="button"
        className={`${ui.control} flex min-w-[14rem] items-center justify-between gap-2 text-left`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={panelId}
        aria-labelledby={`${labelId} ${idPrefix}-categories-trigger-text`}
        onClick={() => setOpen(o => !o)}
      >
        <span id={`${idPrefix}-categories-trigger-text`} className="truncate">{summaryLabel}</span>
        <span aria-hidden="true" className="text-muted">▾</span>
      </button>

      {open && (
        <div
          id={panelId}
          className="absolute z-10 mt-1 w-72 rounded-md border border-border-default bg-surface-alt p-2 shadow-lg ring-1 ring-black ring-opacity-5"
        >
          {selected.length > 0 && (
            <button
              type="button"
              className="mb-1 w-full rounded px-2 py-1 text-left text-xs font-medium text-blue-600 hover:bg-surface-hover"
              onClick={() => onChange([])}
            >
              Clear selection
            </button>
          )}
          <div className="max-h-64 space-y-0.5 overflow-y-auto">
            {activeCategories.map(c => (
              <label
                key={c.CategoryID}
                htmlFor={`${idPrefix}-cat-${c.CategoryID}`}
                className="flex items-center gap-2 rounded px-2 py-1 text-sm text-secondary hover:bg-surface-hover"
              >
                <input
                  id={`${idPrefix}-cat-${c.CategoryID}`}
                  type="checkbox"
                  className={ui.checkbox}
                  checked={selected.includes(c.CategoryID)}
                  onChange={() => toggle(c.CategoryID)}
                />
                {c.Code} — {c.Name}
              </label>
            ))}
            {activeCategories.length === 0 && <p className="px-2 py-1 text-sm text-muted">No active categories.</p>}
          </div>
          <p className="mt-2 border-t border-border-default px-2 pt-2 text-xs text-muted">
            No selection = all calls, including uncategorized. Selecting one or more categories
            excludes calls with no category assigned.
          </p>
        </div>
      )}
    </div>
  );
};
